/**
 * Shared listing-meta fetch + adapter used by single-item checkout sheets
 * (offer settle, auction winner settle). Domain-pure: takes a listing id,
 * returns parsed meta or null on fail-open. No React, no component state.
 *
 * Why a single helper:
 *  - Two consumers used to ship parallel `mapMetaShippingToMethod` helpers
 *    with subtly different drift surfaces (one typed via inline shape, one
 *    via a local interface). Both feed the same `CartItemData` shape.
 *  - Boundary parse goes through `ListingMetaResponseSchema` from
 *    `@kraahl/contract` so a wire-shape drift surfaces at the boundary, not
 *    in render.
 *
 * Fail-open behaviour preserved from both callsites: on network/parse error
 * the caller can show "no shipping methods / no rails" copy, which is the
 * existing merchant-not-ready path.
 *
 * @see components/checkout/payment-sheet.tsx — sole consumer
 * @see packages/contract/src/listing-meta.ts — wire schema
 */
import {
  ListingMetaResponseSchema,
  parseApiResponse,
  type ListingMetaResponse,
  type ShippingRatesParcel,
} from "@kraahl/contract"
import type { ShippingMethod } from "@/components/cart/types"

export interface CheckoutMeta {
  shippingMethods: ShippingMethod[]
  paypalMerchantId: string | null
  sellerCheckoutNote: string | null
  paypalCapabilities: string[]
  availableProviders: ("paypal" | "stripe")[]
  /**
   * WS4.7 — Shippo live-rate routing surface. Forwarded to `PayPalCheckout`'s
   * triple-guard (`shippingMode === 'live_rate' && parcel != null &&
   * sellerDetails[0]?.sellerId !== undefined`) so single-item offer/auction
   * checkouts flip into the address-first sub-flow on parity with cart.
   * `null` here = legacy listing OR live-rate listing with incomplete dims
   * (caller treats as "cannot live-rate quote", falls through to flat-rate).
   *
   * @see components/cart/paypal-checkout.tsx:127 — triple-guard
   * @see packages/contract/src/listing-meta.ts ListingMetaResponse.parcel
   */
  shippingMode: "legacy" | "live_rate" | null
  parcel: ShippingRatesParcel | null
  /** Buyer-display currency (uppercase ISO-4217, e.g. "USD"). */
  currency: string
}

/**
 * Adapter: `ListingMetaResponse.parcel` carries snake_case inner keys
 * (`mass_unit`, `distance_unit`) because the wire schema mirrors the DB
 * column names. `ShippingRatesParcel` (the prop type PayPalCheckout consumes
 * via the Shippo sub-flow) carries camelCase (`massUnit`, `distanceUnit`).
 * Pure shape rename — no value coercion.
 */
function metaParcelToShippingRatesParcel(
  parcel: ListingMetaResponse["parcel"],
): ShippingRatesParcel | null {
  if (parcel == null) return null
  return {
    weight: parcel.weight,
    length: parcel.length,
    width: parcel.width,
    height: parcel.height,
    massUnit: parcel.mass_unit,
    distanceUnit: parcel.distance_unit,
  }
}

export function mapMetaShippingToMethod(m: {
  id: string
  name: string
  description?: string
  price: number
  extraValue?: number
  allowCombinedShipping?: boolean
  bannedCountries?: string[]
}): ShippingMethod {
  return {
    id: m.id,
    name: m.name,
    description: m.description ?? "",
    price: m.price,
    extraValue: m.extraValue ?? 0,
    allowCombinedShipping: m.allowCombinedShipping ?? false,
    combinedFee: m.extraValue ?? 0,
    bannedCountries: m.bannedCountries ?? [],
  }
}

export function metaResponseToCheckoutMeta(metaRes: ListingMetaResponse | null): CheckoutMeta {
  const methods = (metaRes?.shippingMethods ?? []).map(mapMetaShippingToMethod)
  const paypalMerchantId = metaRes?.paypalMerchantId ?? null
  const sellerCheckoutNote =
    typeof metaRes?.sellerCheckoutNote === "string" && metaRes.sellerCheckoutNote.trim()
      ? metaRes.sellerCheckoutNote.trim()
      : null
  const paypalCapabilities = metaRes?.paypalCapabilities ?? []
  const availableProviders = metaRes?.availableProviders ?? []
  const shippingMode = metaRes?.shippingMode ?? null
  const parcel = metaParcelToShippingRatesParcel(metaRes?.parcel ?? null)
  const currency = metaRes?.currency ?? "USD"
  return {
    shippingMethods: methods,
    paypalMerchantId,
    sellerCheckoutNote,
    paypalCapabilities,
    availableProviders,
    shippingMode,
    parcel,
    currency,
  }
}

export async function fetchListingMeta(listingId: string): Promise<CheckoutMeta | null> {
  try {
    const r = await fetch(
      `/api/listing-meta?listingId=${encodeURIComponent(listingId)}`,
      { credentials: "include" },
    )
    if (!r.ok) return null
    const parsed = await parseApiResponse(r, ListingMetaResponseSchema)
    return metaResponseToCheckoutMeta(parsed)
  } catch {
    return null
  }
}
